import { DLMApplication, DLMLabel, DLMLabelSelectionDetails, DLMROIDetails, DLMType, DLMDOC, DLMROI, DLMSiteApplication } from "./_models";
import { DLM_APPLICATION_TYPES, DLM_ROIDETAIL_FIELDS, DLM_ROI_GENERATE_FIELDS, DLM_TYPES, DOC_MODEL_FIELDS, DLM_ROI_FILE_FIELDS } from "./_constants";
import { cloneDeep, pickBy, filter, orderBy, findIndex, remove, pick } from 'lodash';
import { array_fill_keys, isPopulatedArray } from "../../../components/helpers";

const getDLMWorkflowCode = (dat: DLMApplication) => {

  if (! dat) return '';

  const { dlmType, applicationType } = dat;

  switch(applicationType) {
    case "TYPE_APPLICATION":
    case "TYPE_DOCINCREASE":
      if (dlmType) {
        const { workflow, mandatory } = dlmType;
        switch (workflow) {
          case 'WORKFLOW_LABEL5':
            return 'Label5';
          case 'WORKFLOW_LABEL1B1':
            return 'Label1b1';
          case 'WORKFLOW_LABEL1B2':
            return 'Label1b2';
          case 'WORKFLOW_LABEL1B3':
            return 'Label1b3';
          case 'WORKFLOW_DOC1B':
            return 'MandatoryDOC1B';
          case 'WORKFLOW_DOC':
            return mandatory ? 'MandatoryDOC' : 'DOC';
          default:
            break;
        }
      }
      break;

    case "TYPE_SITEINSPECTION":
      return 'SiteInspect';
    case "TYPE_TRIGGERTEST":
      return 'TriggerTest';
    case "TYPE_LABELINSPECTION":
      return 'LabelInspect';

    case "TYPE_LABELREPLACEMENT":
      return 'LabelReplacement';
    case "TYPE_EDIT":
      return 'LabelEdit';
    case "TYPE_SPLIT":
      return 'LabelSplit';
    case "TYPE_DOCEDIT":
      return "DOCEdit";
    case "TYPE_DOCSPLIT":
      return "DOCSplit";
    case "TYPE_ROIEDIT":
      return "ROIEdit";
    case "TYPE_ROISPLIT":
      return "ROISplit";
    default:
      break;
  }

  return '';
}


const getDLMApplicationTypeText = (applicationType: string, dlmType: DLMType | undefined, status?: string) => {
  let appTypeText = (applicationType && DLM_APPLICATION_TYPES.hasOwnProperty(applicationType)) ? DLM_APPLICATION_TYPES[applicationType as keyof typeof DLM_APPLICATION_TYPES] : '';
  if (applicationType === 'TYPE_APPLICATION' && dlmType && dlmType?.type && DLM_TYPES.hasOwnProperty(dlmType.type)) {
    appTypeText = `${DLM_TYPES[dlmType.type as keyof typeof DLM_TYPES]} ${appTypeText}`;
  }

  if (applicationType === 'TYPE_DOCINCREASE' && dlmType) {
    const workflow = getDLMWorkflowCode({applicationType, dlmType} as DLMApplication);
    if (workflow) {
      appTypeText += ` (${workflow})`;
    }
  }

  if (appTypeText === 'Label Application' && status) {
    appTypeText += ' - ' + status;
  }
  return appTypeText;
}


const getLastPathFromWorkflow = (workflow: string) => {
  let lastPath = '';
  switch (workflow) {
    case 'SiteInspect':
    case 'TriggerTest':
    case 'LabelInspect':
      lastPath='siteinspection-applications-list';
      break;
    case 'DOC':
    case 'MandatoryDOC':
      lastPath='doc-applications-list';
      break;
    case 'Label1b1':
    case 'Label1b2':
    case 'Label1b3':
    case 'Label5':
    case 'ROIEdit':
    case 'ROISplit':
    case 'LabelReplacement':
      lastPath='roi-applications-list';
      break;
    default:
      break;
  }
  return lastPath;
}

const SITE_INSPECTION_FIELDS = [
  "prototypeTestReport",
  "trainingRecord",
  "methodOfStatement",
  "productConstructionDrawing",
  "sitePlanDrawing",
  "siteChecklist",
  "setscoROI",
];


const SIGNED_DOC_FIELDS = [
  'docId',
  'docAttachmentId',
  'issuedDate'
];

const ROI_MODEL_FIELDS = [
  'roiId',
  'roiAttachmentId',
  'issueDate'
];

// function to normalize docModels info
const cleanupDataDOCModel = (data: object) => {

  //@ts-ignore
  if (! (data && data.hasOwnProperty('docModels'))) return; // do nothing

  //@ts-ignore
  if (data.hasOwnProperty('approved') && (! data.approved)) {
    //@ts-ignore
    delete data.docModels;
    return;
  }

  //@ts-ignore
  if (data.docModels.length > 0) {
    //@ts-ignore
    let _filtered = data.docModels.map( (o: any) => pickBy(o, (value, key) => (value && DOC_MODEL_FIELDS.includes(key))));
    Object.assign(data, {docModels: _filtered})
  }

}

const cleanupDataSiteROIModel = (data: any) => {
  //@ts-ignore
  if (! (data && typeof(data) === 'object' && data.hasOwnProperty('siteROIModels'))) return; // do nothing

  const siteROIModels = cloneDeep(data.siteROIModels);
  delete data.siteROIModels;

  if (!(siteROIModels && siteROIModels.length > 0) || (data.hasOwnProperty('approved') && (! data.approved) )) {
    return;
  }

  let roiModels: Array<any> = [];
  siteROIModels.forEach((site: any) => {
    if (! (site?.roiModels && site.roiModels.length > 0)) return;
    site.roiModels.forEach( (roi: any) => {
      if (! (roi?.roiId && roi?.roiAttachmentId)) return;
      roiModels.push(pickBy(roi, (value, key) => (value && ROI_MODEL_FIELDS.includes(key))));
    })
  })

  if (roiModels.length > 0) {
    Object.assign(data, { roiModels: orderBy(roiModels, ['no'],['asc']) });
  }
}

// used by Applicant:Pending Inspection Label 5
const cleanupDataSiteDetails = (data: any, workflow: any) => {

  //@ts-ignore
  if (! (data && typeof(data) === 'object' && data.hasOwnProperty('siteDetails'))) return; // do nothing

  const siteDetails = cloneDeep(data.siteDetails);
  delete data.siteDetails;

  if (!(siteDetails && siteDetails.length > 0)) {
    return;
  }

  let dlmWebPhase0SitesModel: Array<any> = [];

  siteDetails.forEach((_site: any) => {
    const _result = pickBy(_site, (value, key) => (value && SITE_INSPECTION_FIELDS.includes(key)));
    if (_result && Object.keys(_result).length > 0) {
      dlmWebPhase0SitesModel.push({..._result, id: _site.siteId});
    }
  })

  if (dlmWebPhase0SitesModel.length > 0) {
    Object.assign(data, { dlmWebPhase0SitesModel: (workflow === 'Label5') ? dlmWebPhase0SitesModel[0] : dlmWebPhase0SitesModel });
  }
}

const cleanupSignedDOCModels = (data: any) => {
  if (! (data && typeof(data) === 'object' && data.hasOwnProperty('docModels') && data.docModels.length > 0) ) return; // do nothing
  Object.assign(data, { docModels : data.docModels.map((_doc: any) => pick(_doc, SIGNED_DOC_FIELDS))});
}

// used for ROI Replace/Split, labelsNotUsed
const extractLabelSelectionDetails = (roiDetails: Array<DLMROIDetails>) => {

  if (! (roiDetails && roiDetails.length > 0)) return [];

  const _labelDetails: Array<DLMLabelSelectionDetails> = [];

  roiDetails.forEach( _detail => {
    if (! (_detail.hasOwnProperty('labels') && _detail.labels.length > 0)) return

    // extract template
    let prefix = '';
    let places = 5;

    if (_detail.hasOwnProperty('labelFrom') && _detail.labelFrom) {
      const _matches = _detail.labelFrom.match(/^(\D*)(\d*)$/);
      if (_matches && _matches.length > 2) {
        prefix = _matches[1];
        places = _matches[2].length;
      }
    }

    // extract labels
    const labelCodes = filter(_detail.labels, (o) => { return (! o.deleted); }).map(
      (label: DLMLabel) => (prefix + (`${label.serialNumber}`).padStart(places, '0'))
    ).sort();

    if (labelCodes.length > 0) {
      _labelDetails.push({
        targetDetailId: _detail?.id ? _detail.id : 0,
        labelFrom: _detail.labelFrom,
        labelTo: _detail.labelTo,
        labelCodes,
        targetLabels: null
      })
    }
  })

  return _labelDetails;
}


const extractDOCModels = (dat: DLMApplication) => {

  if (! (dat && dat.siteApplications && dat.siteApplications.length > 0)) return [];

  const _sites = filter(dat.siteApplications, (o) => { return (! o.deleted); });
  if (! (_sites && _sites.length > 0)) {
    return [];
  }

  let _docModels: any = [];

  _sites.forEach(site => {

    const _siteBase = {
      siteApplicationId: site.id,
      projectName: site.projectName,
      location: site.projectLocation,
      installer: site.installerName,
      otherRef: site.otherReference,
      hourRating: site.hourRating,
      unit: site.unit,
      docId: 0,
      docAttachmentId:0,
      docAttachmentIdAttachmentName: ''
    };

    if (! (site.hasOwnProperty('docs') && site.docs.length > 0)) {
      _docModels.push(_siteBase);
      return _docModels;
    }

    const _docs = filter(site.docs,  (o) => { return (! o.deleted); });

    if (! isPopulatedArray(_docs)) {
      _docModels.push(_siteBase);
      return _docModels;
    }

    _docs.forEach( _doc => {
      _docModels.push({
        ..._siteBase,
        ...pick(_doc,DOC_MODEL_FIELDS),
        siteApplicationId: site.id,
        docId: _doc.id,
        docAttachmentId:0,
        docAttachmentIdAttachmentName: ''
      })
    })
  })

  return orderBy(_docModels,['cbRef'], ['asc']);
}


const extractSiteROIModels = (dat: DLMApplication) => {

  if (! (dat && dat.siteApplications && dat.siteApplications.length > 0)) return [];

  const _sites = filter(dat.siteApplications, (o) => { return (! o.deleted); });
  if (_sites.length === 0) {
    return [];
  }

  const { state, applicationType } = dat;

  let _siteROIModels: any = [];

  _sites.forEach(site => {
    let _payload = extractModelFromSite(site, state, applicationType);

    if (_payload) {
      _siteROIModels.push(_payload);
    }
  })
  return _siteROIModels;
}

const extractModelFromSite = (site: DLMSiteApplication, state: string, applicationType: string) => {

  if (! (site.hasOwnProperty('rois') && site.rois.length > 0)) return null;

  const _rois = filter(site.rois,  (o) => { return (! o.deleted); });

  if (state === 'Site Inspection' && applicationType === 'TYPE_LABELREPLACEMENT') {
    remove(_rois, (o) => (! (o.selectable)));
  }

  if (_rois.length === 0)  return;

  const roiModels = _rois.map( o => {

    let roiDetails = filter(o.roiDetails, (obj) => { return (! obj.deleted); }).map(det => {
      const _detail = pick(det, DLM_ROIDETAIL_FIELDS);
      if (state === 'Site Inspection') {
        Object.assign(_detail, { labels: applicationType === 'TYPE_LABELREPLACEMENT' ? det.draftLabels : det.labels});
      }
      return _detail;
    });

    return {
      ...pick(o, [...DLM_ROI_GENERATE_FIELDS, ...DLM_ROI_FILE_FIELDS]),
      roiDetails,
      roiId: o.id,
      roiAttachmentId:0,
      roiAttachmentIdAttachmentName: ''
    }
  });

  const _payload = {
    siteId: site.id,
    projectName : site.projectName,
    clientRefNo: site.clientRefNo,
    roiModels: orderBy(roiModels, ['cbRef','series','no','split'], ['asc','asc','asc','asc']),
  }
  return _payload;
}


const extractAdminReviewSites = (dat: DLMApplication) => {


  if (! (dat && dat.siteApplications && dat.siteApplications.length > 0)) return [];

  const _sites = filter(dat.siteApplications, (o) => { return (! o.deleted); });
  if (_sites.length === 0) {
    return [];
  }

  return _sites.map(site => ({
    siteApplicationId : site.id,
    projectName: site.projectName,
    projectLocation: site.projectLocation,
    inspectorID: 0,
    inspectionDate: '',
    note: '',
    noteAttachments: [],
    roiModels : [],
  }))
}



const MAP_APP2DOC_FIELDS = {
  applicationCertificate: 'applicationCertificate',
  model: 'model',
  hourRating: 'hourRating',
  projectLocation: 'location',
  scdfProjectPlanNo: 'planNo',
  converageArea: 'areaOfCoverage',
  unit: 'unit',
  otherRef: 'otherRef',
  comment: 'comment',
  ///
  applicationType: 'applicationType',
  docSplitArea: 'docSplitArea',
  disclaimerAck: 'disclaimerAck',
}

const APPSITE_DOC_FIELDS = {
  id: 'siteId',
  installerName: "installer",
  projectName: "projectName",
  projectLocation: "location",
  bpNumber: "planNo",
  otherReference: 'otherRef',
  coverageArea: 'areaOfCoverage',
  unit: 'unit',
  hourRating: 'hourRating',
}

const APPSITE_DOC_FILE_FIELDS = [
  'id',
  'cbRef',
  'issueDate',
  'draftDOCFile',
  'actualDOCFile',
  'signedDOCFile'
]



const convertDLMApp2DOCDetails = (dat: DLMApplication) => {
  let _detail = {
    ...array_fill_keys(['issueDate','companyName','model','series','no','cbRef','applicationType','unit','hourRating'],''),
    ...array_fill_keys(['areaOfCoverage','id','no'],0),
    ...pickBy(dat, (value, key) => (key.match(/Changed$/))),  // add Changed fields
    ...pick(dat, ['applicationType']),
  }
  // map listed fields
  for (const [key, fld] of Object.entries(MAP_APP2DOC_FIELDS)) {
    if (dat.hasOwnProperty(key)) {
      Object.assign(_detail, { [fld] : dat[key as keyof typeof dat]})
    }
  }

  if (dat?.applicationCertificate && dat?.applicationCertificate?.companyName) {
    Object.assign(_detail, { companyName: dat.applicationCertificate.companyName});
  }

  if (dat?.targetDOC) {
    Object.assign(_detail, pick(dat.targetDOC, ['areaOfCoverage','model','id','series','no','cbRef','companyName']))
  }

  if (dat.applicationType !== 'TYPE_DOCSPLIT' && dat?.siteApplications && isPopulatedArray(dat.siteApplications)) {

    const _sites = orderBy(filter(dat.siteApplications, (o:any) => (! o?.deleted)), ['id'],['desc'])

    if (_sites && _sites.length > 0) {
      const _site = _sites[0];
      Object.keys(APPSITE_DOC_FIELDS).forEach((key: string) => {
        //@ts-ignore
        if (_site.hasOwnProperty(key) && _site[key]) {
          //@ts-ignore
          Object.assign(_detail, {[APPSITE_DOC_FIELDS[key]] : _site[key]})
        }
      })
      Object.assign(_detail, {
        otherReference: _site?.otherReference ? _site.otherReference : ''
      });

      if (_site?.docs && _site.docs.length > 0) {
        Object.assign( _detail, {docs:
          orderBy(filter(_site.docs, (o : any) => (! o?.deleted)).map(
            dat => {
              //@ts-ignore
              return pickBy(dat, (value,key) => (value && APPSITE_DOC_FILE_FIELDS.indexOf(key) > -1))
            }
          ),['cbRef'],['asc'])
        });
      }
    }
  }

  if (dat.hasOwnProperty('otherAttachments') && dat.otherAttachments.length > 0) {
    Object.assign(_detail, {otherAttachments: dat.otherAttachments.map(o => { return { id: o.id, name: o.fileName, fileName: o.fileName}} )});
  }

  return _detail as DLMDOC;
}

const APP_ROI_COMMON_FIELDS = [
  'applicationCertificate',
  'model',
  'hourRating',
  'otherRef',
  'comment',
  'roiSplitArea',
  'policeReport',
  'disclaimerAck',
  'inspectionDate',
]

const APP_ROI_MAP_FIELDS = {
  projectLocation: 'location',
  scdfProjectPlanNo: 'planNo',
  totalLabelQuantity: 'quantity',
}



const APPSITE_ROI_FIELDS = {
  id: 'siteId',
  installerName: "installerCompany",
  bpNumber: "planNo",
  otherReference: 'otherRef',
  clientRefNo: 'cocHolderRef',

}

const APPSITE_ROI_FILE_FIELDS = [
  'id',
  'cbRef',
  'issueDate',
  'draftROIFile',
  'actualROIFile',
  'signedROIFile'
]



const convertDLMApp2ROIDetails = (dat: DLMApplication) => {

  let _detail = {
    ...array_fill_keys(['issueDate', 'companyName', 'model', 'series', 'cbRef', 'projectName', 'inspectionDate','hourRating','otherRef'],''),
    ...array_fill_keys(['no','id','quantity','roiSplitArea'],0),
    disclaimerAck: false,
    ...pickBy(dat, (value, key) => (APP_ROI_COMMON_FIELDS.includes(key) || key.match(/Changed$/))),  // add common and Changed fields
  };

  if (dat?.targetROI) {
    Object.assign(_detail, pick(dat.targetROI, ['quantity','model','id','series','no','cbRef','projectName']))
  }

  if (dat?.applicationCertificate && dat?.applicationCertificate?.companyName) {
    Object.assign(_detail, {companyName: dat.applicationCertificate.companyName});
  }

  Object.keys(APP_ROI_MAP_FIELDS).forEach((key: string) => {
    //@ts-ignore
    if (dat.hasOwnProperty(key) && dat[key]) {
      //@ts-ignore
      Object.assign(_detail, {[APP_ROI_COMMON_FIELDS[key]] : dat[key]})
    }
  })

  if (dat?.siteApplications && dat.siteApplications.length > 0) {
    const _sites = orderBy(filter(dat.siteApplications, (o:any) => (! o?.deleted)), ['id'],['desc'])

    if (_sites && _sites.length > 0) {
      const _site = _sites[0];
      Object.keys(APPSITE_ROI_FIELDS).forEach((key: string) => {
        //@ts-ignore
        if (_site.hasOwnProperty(key) && _site[key]) {
          //@ts-ignore
          Object.assign(_detail, {[APPSITE_ROI_FIELDS[key]] : _site[key]})
        }
      })
      Object.assign(_detail, {
        otherReference: _site?.otherReference ? _site.otherReference : '',
        siteApplication: _site
      });

      if (_site?.projectName &&  _site?.projectLocation ) {
        Object.assign(_detail, {
          projectName: [_site.projectName, _site.projectLocation].join('/'),
        });
      }

      if (_site?.rois && _site.rois.length > 0) {
        Object.assign( _detail, {rois:
          orderBy(filter(_site.rois, (o : any) => (! o?.deleted)).map(
            dat => {
              //@ts-ignore
              return pickBy(dat, (value,key) => (value && APPSITE_ROI_FILE_FIELDS.indexOf(key) > -1))
            }
          ),['cbRef'],['asc'])
        });
      }

      if (_site?.siteApplicationDetails && _site.siteApplicationDetails.length > 0) {
        Object.assign(_detail, { roiDetails : filter(_site.siteApplicationDetails, (o) => (! o?.deleted))});
      }
    }
  }

  if (dat.hasOwnProperty('otherAttachments') && dat.otherAttachments.length > 0) {
    Object.assign(_detail, {otherAttachments: dat.otherAttachments.map(o => { return { id: o.id, name: o.fileName, fileName: o.fileName}} )});
  }

  //@ts-ignore
  return _detail as DLMROI;
}

const convertSplitApp2ROIDetails = (dat: DLMApplication) => {

  if (! (dat && dat?.targetROI && dat.targetROI?.id > 0)) return null;

  let _detail = cloneDeep(dat.targetROI);

  Object.assign(_detail, {
    comment: dat?.comment ? dat.comment : '',
  })

  if (dat.hasOwnProperty('policeReport') && dat.policeReport?.id)  {
    Object.assign(_detail, { policeReport : dat.policeReport.id, policeReportAttachmentName : dat.policeReport.fileName});
  }

  if (dat.hasOwnProperty('otherAttachments') && dat.otherAttachments.length > 0) {
    Object.assign(_detail, {otherAttachments: dat.otherAttachments.map(o => { return { id: o.id, name: o.fileName}} )});
  }

  let _targets: Array<any> = [];
  if (dat.applicationType !== 'TYPE_DOCSPLIT' && dat?.siteApplications && dat.siteApplications.length > 0) {
    const _sites = filter(dat.siteApplications, (o) => (! o.deleted));
    if (_sites && _sites.length > 0) {
      const _site = _sites[0];
      Object.assign(_detail, {siteId: _site.id});
      if (_site?.siteApplicationDetails && _site.siteApplicationDetails.length > 0) {
        _targets = filter(_site.siteApplicationDetails, (o) => (! o?.deleted)).map(_siteDetail => {
          const _targetDetail = _siteDetail.targetDetail;
          let prefix = '';
          let places = 6;
          if (_targetDetail.hasOwnProperty('labelFrom') && _targetDetail.labelFrom) {
            const _matches = _targetDetail.labelFrom.match(/^(\D*)(\d*)$/);
            if (_matches && _matches.length > 2) {
              prefix = _matches[1];
              places = _matches[2].length;
            }
          }

          const targetLabels = filter(_siteDetail.targetLabels, (o) => { return (! o.deleted); }).map(
            label => (`${prefix}${label.serialNumber.toString().padStart(places, '0')}`)
          ).sort();

          return {
            siteDetailId: _siteDetail.id,
            targetDetailId: _targetDetail.id,
            targetLabels
          }
        })
      }
    }
  }

  if (_targets && _targets.length > 0) {
    let _roiDetails = cloneDeep(_detail.roiDetails);

    _targets.forEach(_target => {
      const _idx = findIndex(_roiDetails, {id: _target.targetDetailId});
      if(_idx > -1) {
        Object.assign(_roiDetails[_idx], _target);
      }
    })

    Object.assign(_detail, {roiDetails: _roiDetails});
  }

  return _detail;
}

export {
  getDLMWorkflowCode, getDLMApplicationTypeText, getLastPathFromWorkflow,
  cleanupDataDOCModel, cleanupDataSiteROIModel, cleanupDataSiteDetails,
  cleanupSignedDOCModels,
  extractLabelSelectionDetails,
  extractDOCModels, extractSiteROIModels,extractModelFromSite, extractAdminReviewSites,
  convertDLMApp2DOCDetails,
  convertDLMApp2ROIDetails, convertSplitApp2ROIDetails,
}